import cx from 'classnames';

import { PricingPlanFeature, PricingPlanInterval, PricingPlanSchema } from 'types';
import { displayPrice, mapPricingPlanFeature, mapPricingPlanInterval } from 'utils';
import { profileChannelNameSelector } from 'store/selectors';
import { useStore } from 'hooks';

import Feature from './Feature';

import styles from './PricingPlanCard.module.scss';

export type PricingPlanCardProps = PricingPlanSchema & {
  isCurrentPlan?: boolean;
  isPurchasable?: boolean;
  onActivateFreeTrial?(): void;
  onPurchase?: (id: string) => void;
};

const displayPriceAndInterval = (price: number, interval: PricingPlanInterval) =>
  `$${displayPrice(price)} per ${mapPricingPlanInterval(interval)}`.toLocaleLowerCase();

const PricingPlanCard = ({
  id,
  name,
  price,
  interval,
  features,
  settings,
  isCurrentPlan = false,
  isPurchasable = false,
  onActivateFreeTrial,
  onPurchase
}: PricingPlanCardProps): JSX.Element => {
  const { profileChannelName } = useStore(state => ({
    profileChannelName: profileChannelNameSelector(state)
  }));

  const isFreeTrial = price === 0;
  const isPaidPlan = !isFreeTrial;

  const handleOnActivateFreeTrial = () => onActivateFreeTrial?.();

  const handleOnClick = () => onPurchase?.(id);

  const renderActionButton = () => {
    if (isFreeTrial && profileChannelName) return null;
    if (isFreeTrial && !profileChannelName) {
      return (
        <button type="button" className={styles.purchase} onClick={handleOnActivateFreeTrial}>
          Start free trial
        </button>
      );
    }
    if (isCurrentPlan || !isPurchasable) return null;

    return (
      <button type="button" className={styles.purchase} onClick={handleOnClick}>
        Purchase {name}
      </button>
    );
  };

  console.log('DEBUG: ', Object.keys(PricingPlanFeature));

  return (
    <div className={cx(styles.container, { [styles.current]: isCurrentPlan })}>
      <div className={styles.header}>
        <p className={styles.title}>{name}</p>
        <ul className={cx(styles.price, { [styles.current]: isCurrentPlan })}>
          {isCurrentPlan && (
            <li>
              <span>Current plan</span>
            </li>
          )}
          <li>{isPaidPlan ? <span>{displayPriceAndInterval(price, interval)}</span> : <span>free</span>}</li>
        </ul>
      </div>

      <ul className={styles.features}>
        {settings.STREAM_DURATION && (
          <li>
            <Feature available>{settings.STREAM_DURATION / 60} Streaming hours</Feature>
          </li>
        )}

        {Object.keys(PricingPlanFeature).map(feature => (
          <li key={feature}>
            <Feature available={features.includes(feature as PricingPlanFeature)}>
              {mapPricingPlanFeature(feature as PricingPlanFeature)}
            </Feature>
          </li>
        ))}
      </ul>

      {renderActionButton()}
    </div>
  );
};

export default PricingPlanCard;
